// Manual invoice-snapshot entry — for historical months where what was
// actually invoiced doesn't match what today's formulas/rates would produce
// (e.g. IIL's July/August invoices embedded a 7.4807% recovery add-back,
// computed assuming a 6% client WHT deduction, before the rate was corrected
// to 7% -> 8.8376%; IIL's invoice-building code hardcodes its gross-up rate,
// so re-running it today can never reproduce that historical number). Writes
// directly to approved_invoice_snapshots with the real figures finance
// already knows from what was actually sent to the client, bypassing the
// normalize/calculate pipeline entirely for this one doc.
//
// A saved manual entry can be corrected afterwards (updateManualInvoiceSnapshot):
// finance only, a reason is mandatory, and the before/after is logged in
// invoice_snapshot_corrections.
import { doc, getDoc, getDocs, collection, query, runTransaction, serverTimestamp, where } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { buildManualFields, describeChanges, snapshotIdFor } from "./manualSnapshotLogic";
import { getActiveProfile } from "./clientTaxProfileService";
import { assertPeriodOpen } from "./periodLockService";

const SNAPSHOTS = "approved_invoice_snapshots";
const CORRECTIONS = "invoice_snapshot_corrections";

export async function createManualInvoiceSnapshot(input) {
  const base = buildManualFields(input);
  await assertPeriodOpen(base.accountingPeriod);
  const profile = await getActiveProfile(base.clientCode);
  const snapshotRef = doc(db, SNAPSHOTS, snapshotIdFor(base.invoiceNumber));
  const fields = {
    ...base,
    paymentTermsDays: Number(profile?.paymentTermsDays ?? 30),
    approvedBy: auth.currentUser?.uid || null,
    approvedByEmail: auth.currentUser?.email || null,
    approvedAt: serverTimestamp()
  };

  await runTransaction(db, async (transaction) => {
    const existing = await transaction.get(snapshotRef);
    // Refuses to clobber a real automated approval — manual entry is only
    // for filling a gap or correcting an earlier manual entry, never for
    // overwriting a snapshot the approval pipeline actually produced.
    if (existing.exists() && !existing.data().enteredManually) {
      throw new Error(`${base.invoiceNumber} already has an automated approval snapshot for ${base.clientCode}. Manual entry would overwrite it — that's blocked. If this needs correcting, use the invoice's own approval flow instead.`);
    }
    transaction.set(snapshotRef, fields);
  });

  return fields;
}

export async function listManualEntries(clientCode) {
  const q = query(
    collection(db, SNAPSHOTS),
    where("clientCode", "==", clientCode),
    where("sourceCollection", "==", "manual_entry")
  );
  const result = await getDocs(q);
  return result.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// What already depends on this invoice: receipts applied against it and a WHT
// control record. Used to warn before an edit and to block a renumber.
export async function getInvoiceUsage(invoiceNumber) {
  const id = snapshotIdFor(invoiceNumber);
  const [allocations, whtRecord] = await Promise.all([
    getDocs(query(collection(db, "receipt_allocations"), where("snapshotId", "==", id))),
    getDoc(doc(db, "wht_control_records", id))
  ]);
  const active = allocations.docs.map((d) => d.data()).filter((allocation) => !allocation.reversed);
  return {
    allocationCount: active.length,
    allocatedAmount: active.reduce((sum, allocation) => sum + Number(allocation.allocatedAmount || 0), 0),
    hasWhtRecord: whtRecord.exists()
  };
}

export async function updateManualInvoiceSnapshot({ originalInvoiceNumber, input, changeReason }) {
  if (!String(changeReason || "").trim()) throw new Error("Enter the reason for this correction.");
  const fields = buildManualFields(input);
  await assertPeriodOpen(fields.accountingPeriod);
  const profile = await getActiveProfile(fields.clientCode);
  const originalId = snapshotIdFor(originalInvoiceNumber);
  const newId = snapshotIdFor(fields.invoiceNumber);
  const renumbered = originalId !== newId;

  if (renumbered) {
    const usage = await getInvoiceUsage(originalInvoiceNumber);
    if (usage.allocationCount > 0 || usage.hasWhtRecord) {
      throw new Error(`${originalInvoiceNumber} already has receipts applied or a WHT record, so its number cannot be changed. Edit its figures instead, or reverse the receipt first.`);
    }
  }

  const originalRef = doc(db, SNAPSHOTS, originalId);
  const newRef = doc(db, SNAPSHOTS, newId);
  const who = { uid: auth.currentUser?.uid || null, email: auth.currentUser?.email || null };
  let changes = [];

  await runTransaction(db, async (transaction) => {
    const original = await transaction.get(originalRef);
    const target = renumbered ? await transaction.get(newRef) : null;
    if (!original.exists()) throw new Error(`${originalInvoiceNumber} was not found.`);
    const before = original.data();
    if (!before.enteredManually) throw new Error(`${originalInvoiceNumber} came from an automated approval, not a manual entry. Correct it from its own invoice page instead.`);
    if (fields.clientCode !== before.clientCode) throw new Error("The client of an invoice cannot be changed.");
    if (target?.exists()) throw new Error(`${fields.invoiceNumber} already exists for ${before.clientCode}.`);

    // Moving an invoice out of (or into) a closed month changes that month's P&L.
    if (before.accountingPeriod !== fields.accountingPeriod) await assertPeriodOpen(before.accountingPeriod);
    const saved = {
      ...fields,
      paymentTermsDays: before.paymentTermsDays ?? Number(profile?.paymentTermsDays ?? 30),
      approvedBy: before.approvedBy ?? null,
      approvedByEmail: before.approvedByEmail ?? null,
      approvedAt: before.approvedAt ?? serverTimestamp(),
      editCount: Number(before.editCount || 0) + 1,
      lastEditedAt: serverTimestamp(),
      lastEditedByEmail: who.email,
      lastEditReason: String(changeReason).trim()
    };
    changes = describeChanges(before, saved);
    if (!changes.length) throw new Error("Nothing was changed.");

    if (renumbered) {
      transaction.set(newRef, saved);
      transaction.delete(originalRef);
    } else {
      transaction.set(originalRef, saved);
    }
    transaction.set(doc(collection(db, CORRECTIONS)), {
      kind: "MANUAL_ENTRY_EDIT",
      clientCode: before.clientCode,
      invoiceNumber: fields.invoiceNumber,
      previousInvoiceNumber: before.invoiceNumber,
      monthKey: fields.accountingPeriod,
      previousAmount: Number(before.totals?.grossAmount || 0),
      newAmount: Number(fields.totals.grossAmount || 0),
      reason: String(changeReason).trim(),
      changes,
      before: {
        invoiceNumber: before.invoiceNumber, invoiceDate: before.invoiceDate, accountingPeriod: before.accountingPeriod,
        totals: before.totals || {}, whtModelUsed: before.whtModelUsed || null, manualEntryReason: before.manualEntryReason || ""
      },
      correctedBy: who.uid,
      correctedByEmail: who.email,
      correctedAt: serverTimestamp()
    });
  });

  return { fields, changes, renumbered };
}
